/**
 * The Save-button controller shared by every OVS percentage editor — the
 * district defaults dialog, the campus per-mealtime dialog (including its
 * weekday rules), the Straight Serve milk-split dialog, and the auto-order
 * wizard's percentages step.
 *
 * Sheila, 2026-08-26: *"They should all be editable… And need save button."*
 *
 * ## The model
 *
 * Cells no longer write. Each cell STAGES an edit — the raw string it is
 * showing plus, when the typed value actually changes something, a closure
 * that performs the write. `save()` runs the staged closures, invalidates
 * once, and clears the draft.
 *
 * Why closures rather than a declarative plan: the scope of a write (campus vs
 * district vs one weekday, which order column, which mealtime, rate row vs
 * milk-share set) is knowledge each cell already has and nothing else does.
 * Threading all of it through a central planner would duplicate the cascade in
 * a second place; a closure keeps it exactly where it is used.
 *
 * ## Ordering
 *
 * Writes run SEQUENTIALLY. Milk shares resolve as a whole set per tier, so the
 * first override of a cell writes several rows through the same partial unique
 * indexes; firing them concurrently races those upserts against each other.
 * A dialog is a handful of cells — sequential costs nothing here.
 *
 * ## `stagedRef`
 *
 * Milk cells need to read the WHOLE cell's staged values at save time (see
 * `MilkShareCell`): editing two milk types before pressing Save must not let
 * the second write reinstate the first's inherited value. The ref is the live
 * view of the staged map that those closures read.
 */

import { toast } from '@ordo/ui/sonner'
import { useCallback, useMemo, useRef, useState } from 'react'

import { useInvalidateSplitSettings } from '@/api/location-settings'

/** One cell's pending edit. */
export interface StagedSplitEdit {
  /** The raw string the user typed — the input's controlled value. */
  raw: string
  /** The write to perform on Save; `null` when the edit changes nothing. */
  run: (() => Promise<unknown>) | null
  /** `raw` is neither empty nor a parseable 0–100 percentage. */
  invalid: boolean
}

export interface SplitEditor {
  /** cellKey → pending edit. A cell absent here shows its resolved value. */
  staged: Record<string, StagedSplitEdit>
  /** Live view of `staged`, for save-time closures that must see sibling edits. */
  stagedRef: React.MutableRefObject<Record<string, StagedSplitEdit>>
  stage: (cellKey: string, edit: StagedSplitEdit) => void
  /** Drop every pending edit (Cancel, or discarding on close). */
  discard: () => void
  /** At least one staged edit would actually write. */
  dirty: boolean
  /** At least one staged value can't be parsed — Save is blocked. */
  invalid: boolean
  /** How many cells Save will write. */
  pendingCount: number
  saving: boolean
  /** Returns true when everything was written. */
  save: () => Promise<boolean>
}

export function useSplitEditor(locationId: string): SplitEditor {
  const [staged, setStaged] = useState<Record<string, StagedSplitEdit>>({})
  const stagedRef = useRef<Record<string, StagedSplitEdit>>({})
  const [saving, setSaving] = useState(false)
  const invalidate = useInvalidateSplitSettings()

  const stage = useCallback((cellKey: string, edit: StagedSplitEdit) => {
    stagedRef.current = { ...stagedRef.current, [cellKey]: edit }
    setStaged(stagedRef.current)
  }, [])

  const discard = useCallback(() => {
    stagedRef.current = {}
    setStaged({})
  }, [])

  const entries = useMemo(() => Object.values(staged), [staged])
  const pendingCount = entries.filter((entry) => entry.run !== null).length
  const invalid = entries.some((entry) => entry.invalid)

  const save = useCallback(async (): Promise<boolean> => {
    const runs = Object.values(stagedRef.current)
      .filter((entry) => !entry.invalid && entry.run !== null)
      .map((entry) => entry.run!)
    if (runs.length === 0) {
      discard()
      return true
    }
    setSaving(true)
    try {
      for (const run of runs) {
        await run()
      }
      invalidate(locationId)
      discard()
      toast.success(runs.length === 1 ? 'Percentage saved' : `${runs.length} percentages saved`)
      return true
    } catch (error) {
      // Staged edits are deliberately KEPT on failure — clearing them would
      // silently swallow numbers the user typed, which is the whole complaint.
      toast.error(error instanceof Error ? `Couldn't save: ${error.message}` : "Couldn't save these percentages")
      return false
    } finally {
      setSaving(false)
    }
  }, [discard, invalidate, locationId])

  return { staged, stagedRef, stage, discard, dirty: pendingCount > 0, invalid, pendingCount, saving, save }
}
